import { CreditReport, SpendingLimit, } from "@buildwithflux/models";
import { getEnvironmentName } from "@buildwithflux/shared";
import { CloudFunctionNames } from "./constants";
import { FindEnterpriseOrganizationResponseData, GetAdminStripeCustomerIdResponseData, GetDatasheetUrlResponseData, GetProjectUidForUrlResponseData, GetSharedWorkspacesResponse, } from "./types";
/**
 * The firebase/compat (client SDK v8) version of the functions adapter
 */
export class ClientFunctionsAdapter {
    functionsCentral;
    functionsWest;
    functionsWest1;
    constructor(functionsCentral, functionsWest, functionsWest1) {
        this.functionsCentral = functionsCentral;
        this.functionsWest = functionsWest;
        this.functionsWest1 = functionsWest1;
    }
    cloneDocument(data) {
        return this.callHttpsFunction(`${CloudFunctionNames.cloneDocument}`, "west", data);
    }
    adminCloneDocument(data) {
        return this.callHttpsFunction(CloudFunctionNames.adminCloneDocument, "central", data);
    }
    copyActionRecords(data) {
        return this.callHttpsFunction(CloudFunctionNames.copyActionRecords, "central", data);
    }
    createApiKey() {
        return this.callHttpsFunction(CloudFunctionNames.createApiKey, "west", undefined);
    }
    createLead(data) {
        return this.callHttpsFunction(CloudFunctionNames.createLead, "central", data);
    }
    doCreateProjectChatFromClient(data) {
        return this.callHttpsFunction(CloudFunctionNames.doCreateProjectChatFromClient, "central", data);
    }
    async findEnterpriseOrganization(requestData) {
        return FindEnterpriseOrganizationResponseData.parse((await this.callHttpsFunction(CloudFunctionNames.findEnterpriseOrganization, "west", requestData)).data);
    }
    async getSharedWorkspaces(requestData) {
        return GetSharedWorkspacesResponse.parse((await this.callHttpsFunction(CloudFunctionNames.getSharedWorkspaces, "west", requestData)).data);
    }
    writeUserPresence(data) {
        return this.callHttpsFunction(CloudFunctionNames.writeUserPresence, "central", data);
    }
    retrieveAuditLogForMessage(data) {
        return this.callHttpsFunction(CloudFunctionNames.retrieveAuditLogForMessage, "central", data);
    }
    reportCopilotChatIssue(data) {
        return this.callHttpsFunction(CloudFunctionNames.reportCopilotChatIssue, "central", data);
    }
    startEtlJobForPart(data) {
        return this.callHttpsFunction(CloudFunctionNames.startEtlJobForPart, "west-1", {
            partUid: data.part_uid,
            version: data.version,
        });
    }
    startPdfEtlJob(datasheetUrl) {
        return this.callHttpsFunction(CloudFunctionNames.startPdfEtlJob, "west-1", {
            datasheetUrl,
        });
    }
    startExtractOrderingInformationJob(pdfData, targetOrganizationUid, contentMimeType = "application/pdf") {
        const request = {
            fileContents: pdfData,
            organizationUid: targetOrganizationUid,
            contentMimeType,
        };
        return this.callHttpsFunction(CloudFunctionNames.startExtractOrderingInformationJob, "west", request);
    }
    startImportDatasheetComponentJob(componentInfo, pdfUid, templateProjectUid, organizationUid) {
        const request = {
            pdfUid,
            targetComponent: componentInfo,
            templateProjectUid,
            organizationUid,
        };
        return this.callHttpsFunction(CloudFunctionNames.startImportDatasheetComponentJob, "west", request);
    }
    triggerDesignReview(data) {
        return this.callHttpsFunction(CloudFunctionNames.triggerDesignReview, "west", data);
    }
    async schemaVersion(data) {
        return this.callHttpsFunction(CloudFunctionNames.schemaVersion, "central", data);
    }
    async algoliaAuth(authContext) {
        return this.callHttpsFunction(CloudFunctionNames.algoliaAuth, "central", authContext);
    }
    async typesenseAuth(authContext) {
        return this.callHttpsFunction(CloudFunctionNames.typesenseAuth, "west", authContext);
    }
    async typesenseAdminAuth(authContext) {
        return this.callHttpsFunction(CloudFunctionNames.typesenseAdminAuth, "west", authContext);
    }
    async fluxAuth(data) {
        return this.callHttpsFunction(CloudFunctionNames.fluxAuth, "west", data);
    }
    async createAutoLayoutJob(data) {
        return this.callHttpsFunction(CloudFunctionNames.createAutoLayoutJob, "west", data);
    }
    async updateAccountFollowingState(data) {
        return this.callHttpsFunction(CloudFunctionNames.updateAccountFollowingState, "central", data);
    }
    async getPresignedUrl(data) {
        return this.callHttpsFunction(CloudFunctionNames.getPresignedUrl, "central", data);
    }
    async getProjectUsage(data) {
        return this.callHttpsFunction(CloudFunctionNames.getProjectUsage, "central", data);
    }
    async createStripeCheckoutLink(data) {
        return this.callHttpsFunction(`${CloudFunctionNames.createStripeCheckoutLink}`, "west", data);
    }
    async createStripePortalLink(data) {
        return this.callHttpsFunction(`${CloudFunctionNames.createStripePortalLink}`, "west", data);
    }
    async sendPasswordResetLink(data) {
        return this.callHttpsFunction(CloudFunctionNames.sendPasswordResetLink, "central", data);
    }
    async searchDistributors(data) {
        return this.callHttpsFunction(CloudFunctionNames.searchDistributors, "central", data);
    }
    async renameProject(data) {
        return this.callHttpsFunction(CloudFunctionNames.renameProject, "west", data);
    }
    async restoreProject(data) {
        return this.callHttpsFunction(CloudFunctionNames.restoreProject, "west", data);
    }
    async restoreProjectToNewFromVersion(data) {
        return this.callHttpsFunction(CloudFunctionNames.restoreProjectToNewFromVersion, "west", data);
    }
    async reconstructProject(data) {
        return this.callHttpsFunction(CloudFunctionNames.reconstructProject, "west", data);
    }
    async getProjectName(data) {
        return this.callHttpsFunction(CloudFunctionNames.getProjectName, "west", data);
    }
    /** @inheritdoc */
    async getDatasheetUrl(data) {
        return GetDatasheetUrlResponseData.parse((await this.callHttpsFunction(CloudFunctionNames.getDatasheetUrl, "west", data)).data);
    }
    /** @inheritdoc */
    async getProjectUidForUrl(data) {
        return GetProjectUidForUrlResponseData.parse((await this.callHttpsFunction(CloudFunctionNames.getProjectUidForUrl, "west", data)).data);
    }
    /** @inheritdoc */
    async getProjectUidForUrlAdmin(data) {
        return GetProjectUidForUrlResponseData.parse((await this.callHttpsFunction(CloudFunctionNames.getProjectUidForUrlAdmin, "west", data)).data);
    }
    async getCreditReport(requestData) {
        return CreditReport.parse((await this.callHttpsFunction(CloudFunctionNames.getCreditReport, "west", requestData)).data);
    }
    async getAdminCreditReport(requestData) {
        return CreditReport.parse((await this.callHttpsFunction(CloudFunctionNames.getAdminCreditReport, "west", requestData)).data);
    }
    async getAdminCreditTransactions(requestData) {
        return (await this.callHttpsFunction(CloudFunctionNames.getAdminCreditTransactions, "west", requestData))
            .data;
    }
    async getAdminStripeCustomerId(requestData) {
        return GetAdminStripeCustomerIdResponseData.parse((await this.callHttpsFunction(CloudFunctionNames.getAdminStripeCustomerId, "west", requestData)).data);
    }
    async giveConsentForPaidCredits(data) {
        await this.callHttpsFunction(CloudFunctionNames.giveConsentForPaidCredits, "west", data);
    }
    async snoozePaidCreditConsent(data) {
        await this.callHttpsFunction(CloudFunctionNames.snoozePaidCreditConsent, "west", data);
    }
    async setCreditSpendingLimit(data) {
        return SpendingLimit.parse((await this.callHttpsFunction(CloudFunctionNames.setCreditSpendingLimit, "west", data)).data);
    }
    async setAdminFreeCredits(data) {
        await this.callHttpsFunction(CloudFunctionNames.setAdminFreeCredits, "west", data);
    }
    async updateThreadBriefState(data) {
        await this.callHttpsFunction(CloudFunctionNames.updateThreadBriefState, "west", data);
    }
    async clearBriefPlan(data) {
        await this.callHttpsFunction(CloudFunctionNames.clearBriefPlan, "west", data);
    }
    async fetchSKUs(data) {
        return this.callHttpsFunction(CloudFunctionNames.fetchSKUs, "central", data);
    }
    /** @inheritDoc */
    async reactivateTrial() {
        const result = await this.callHttpsFunction(CloudFunctionNames.reactivateTrial, "west", {});
        return result.data;
    }
    async requestDocumentPricingRefresh(data) {
        return this.callHttpsFunction(CloudFunctionNames.requestDocumentPricingRefresh, "central", data);
    }
    async requestPricingCacheUpdate(data) {
        return this.callHttpsFunction(CloudFunctionNames.requestPricingCacheUpdate, "central", data);
    }
    async requestPricingDataRefresh(data) {
        return this.callHttpsFunction(CloudFunctionNames.requestPricingDataRefresh, "central", data);
    }
    async shadowbanParts(data) {
        return this.callHttpsFunction(CloudFunctionNames.shadowban, "central", data);
    }
    async uploadFileForComment(data) {
        return this.callHttpsFunction(CloudFunctionNames.uploadFileForComment, "west", data);
    }
    async createEducationalPlanCheckout(data = {}) {
        return this.callHttpsFunction(CloudFunctionNames.createEducationalPlanCheckout, "west", data);
    }
    async acceptProjectInvite(data) {
        return this.callHttpsFunction(CloudFunctionNames.acceptProjectInvite, "central", data);
    }
    async sendProjectInviteEmail(data) {
        return this.callHttpsFunction(CloudFunctionNames.sendProjectInviteEmail, "central", data);
    }
    async sendAddUserRoleEmail(data) {
        return this.callHttpsFunction(CloudFunctionNames.sendAddUserRoleEmail, "central", data);
    }
    async sendVerificationEmail(data) {
        return this.callHttpsFunction(CloudFunctionNames.sendVerificationEmail, "central", data);
    }
    async verifyEmailAddress(data) {
        return this.callHttpsFunction(CloudFunctionNames.verifyEmailAddress, "central", data);
    }
    async saveDocumentSnapshot(data) {
        return this.callHttpsFunction(CloudFunctionNames.saveDocumentSnapshot, "central", data);
    }
    async retryMessageGeneration(data) {
        return this.callHttpsFunction(CloudFunctionNames.retryMessageGeneration, "west-1", data, { timeout: 545_000 });
    }
    async triggerCopilotActionGeneration(data) {
        return this.callHttpsFunction(CloudFunctionNames.triggerCopilotActionGeneration, "west-1", data, { timeout: 545_000 });
    }
    async evaluateChatThreadSatisfaction(data) {
        return this.callHttpsFunction(CloudFunctionNames.evaluateChatThreadSatisfaction, "west-1", data);
    }
    async searchSystemKnowledgeModerationQueue(data) {
        return this.callHttpsFunction(CloudFunctionNames.searchSystemKnowledgeModerationQueue, "west-1", data);
    }
    async searchSystemKnowledgeBase(data) {
        return this.callHttpsFunction(CloudFunctionNames.searchSystemKnowledgeBase, "west-1", data);
    }
    async classifyNets(data) {
        return this.callHttpsFunction(CloudFunctionNames.classifyNets, "west-1", data);
    }
    async userSoftDeleteAdmin(data) {
        const result = await this.callHttpsFunction(CloudFunctionNames.userSoftDeleteAdmin, "west", data);
        return result.data;
    }
    async educationalQualificationAdmin(data) {
        const result = await this.callHttpsFunction(CloudFunctionNames.educationalQualificationAdmin, "west", data);
        return result.data;
    }
    async sandbox(data) {
        const result = await this.callHttpsFunction(CloudFunctionNames.sandbox, "west", data);
        return result.data;
    }
    async generateCss(data) {
        const result = await this.callHttpsFunction(CloudFunctionNames.generateCss, "west", data);
        return result.data;
    }
    /** @inheritDoc */
    async endTrialEarly() {
        const result = await this.callHttpsFunction(CloudFunctionNames.endTrialEarly, "west", {});
        return result.data;
    }
    /** @inheritDoc */
    async migrateUserToLatestOnboardingState() {
        const result = await this.callHttpsFunction(CloudFunctionNames.migrateUserToLatestOnboardingState, "central", {});
        return result.data;
    }
    /**
     * Calls a firebase function with the given name, region and data
     *
     * @param name
     * @param region "central" for "us-central1", "west" for "us-west2", "west-1" for "us-west1" - note that we should ALWAYS default to using "west" for new functions
     * @param data
     * @param options
     */
    async callHttpsFunction(name, region, data, options) {
        if (!(region === "central" ? this.functionsCentral : this.functionsWest)) {
            throw new Error(`Tried to call ${name} firebase function in ${region}, but do not have a client context to invoke functions from`);
        }
        return import("firebase/functions").then(async ({ httpsCallable }) => {
            const transaction = await this.startSentryTransaction("httpsCallable-" + name);
            try {
                return await httpsCallable(region === "central"
                    ? this.functionsCentral
                    : region === "west-1"
                        ? this.functionsWest1
                        : this.functionsWest, name, options)({
                    ...data,
                    metadata: {
                        traceId: transaction.traceId,
                        spanId: transaction.spanId,
                        environment: getEnvironmentName(),
                    },
                });
            }
            finally {
                transaction.finish();
            }
        });
    }
    async startSentryTransaction(functionName) {
        const { getCurrentHub, startTransaction } = await import("@sentry/react");
        const transaction = startTransaction({ name: `httpsCallable-${functionName}` });
        const hub = getCurrentHub();
        hub.configureScope((scope) => scope.setSpan(transaction));
        return transaction;
    }
}
//# sourceMappingURL=ClientFunctionsAdapter.js.map